import type { ResponseResult } from '#shared/api';

import type { CaseLegTracking, CaseTrackingResponse } from '../model';

// The BE serves `{ caseId, legs }` bare, with no `{ data, dataGeneratedAt }`
// envelope; `validateAndTransformResponse` takes the envelope. Adapting here is the
// `geoLibraryPayload.ts` pattern, and it keeps the schema describing the wire shape
// rather than a wrapper invented for the validator.

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isLeg = (value: unknown): value is CaseLegTracking =>
  isObject(value) &&
  typeof value.legId === 'string' &&
  isObject(value.departure) &&
  isObject(value.arrival);

/** An unusable payload reads as case id `''`, which no requested case matches. A malformed leg is dropped. */
export const unwrapCaseTracking = (raw: unknown): CaseTrackingResponse => ({
  caseId: isObject(raw) && typeof raw.caseId === 'string' ? raw.caseId : '',
  legs: isObject(raw) && Array.isArray(raw.legs) ? raw.legs.filter(isLeg) : [],
});

export const asCaseTrackingResult = (
  raw: unknown,
): ResponseResult<CaseTrackingResponse> => ({
  data: unwrapCaseTracking(raw),
  errors: null,
  expires: null,
  dataGeneratedAt: null,
  errorsGeneratedAt: null,
});

export const caseTrackingPath = (caseId: string): string =>
  `api/cases/${encodeURIComponent(caseId)}/logistics`;
